import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Grid,
  Group,
  Modal,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconClock,
  IconClockCancel,
  IconDoorEnter,
  IconDoorExit,
  IconEdit,
  IconUsers,
} from '@tabler/icons';
import { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { CustomLoader } from '../../components/custom-loader';
import { Page } from '../../components/page';
import {
  changeScheduled,
  createDailyWorkRequest,
  getLastDailyWorkRequest,
  updateDailyWorkRequest,
} from '../../data/daily-work';
import { useGetEmployee } from '../../data/hooks/employee';
import {
  useGetSectorDailyLog,
  useUpsertSectorDailyLog,
} from '../../data/hooks/sector-daily-logs';
import { useGetUsersPaginated } from '../../data/hooks/user';
import { createOptionRequest, listOptionsRequest } from '../../data/options';
import useAuthContext from '../../hooks/useAuthContext';
import { useDeviceDetector } from '../../hooks/useDeviceDetector';
import { useDisableRightClick } from '../../hooks/useDisableRightClick';
import { useLocation } from '../../hooks/useLocation';
import { DailyWork, DailyWorkType, WorkType } from '../../models/daily-work';
import { EmployeePeriod } from '../../models/employees';
import { UserRole } from '../../models/user';
import { dayjsPlugins, formatLocale } from '../../providers/dayjs-plugins';
import { errorNotification } from '../../providers/mantine-notifications';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';
import { translateServerHttpErrors } from '../../utils/helpers';
import { BankHourRequest } from './components/bank-hour-request';

type ConfirmStateType =
  | 'confirm-begin'
  | 'confirm-start-lounch'
  | 'confirm-end-lounch'
  | 'confirm-finish'
  | null;

export type PageModalState =
  | 'request-bank-hours'
  | 'confirm-daily-work-create'
  | 'review-bank-hours'
  | null;

export function DailyWorkCreate() {
  useDisableRightClick();
  const [pageLoading, setPageLoading] = useState(false);
  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [pageConfirm, setPageConfirm] = useState<ConfirmStateType>(null);
  const [dailyWork, setDailyWork] = useState<DailyWork>();
  const [currentHour, setCurrentHour] = useState<Date>(new Date());
  const [address, setAddress] = useState<string | null>(null);

  const { user, logout } = useAuthContext();
  const { deviceType } = useDeviceDetector();
  const { getLocation } = useLocation();

  const isTimeRegistrationBlocked = user?.user.canRegisterTime === false;

  const canMarkBeginWorkTime = Boolean(
    isTimeRegistrationBlocked ||
      (!!dailyWork?.begin && dailyWork?.type !== WorkType.SCHEDULED) ||
      dailyWork?.type === WorkType.JUSTIFICATION ||
      (dailyWork?.begin && dailyWork?.type === WorkType.WORK),
  );

  const canMarkInitLounchWorkTime = Boolean(
    isTimeRegistrationBlocked ||
      !!dailyWork?.start_lounch ||
      !dailyWork?.begin ||
      !!dailyWork?.finish ||
      dailyWork?.type === WorkType.JUSTIFICATION ||
      (dailyWork?.begin && dailyWork?.type === WorkType.SCHEDULED),
  );

  const canMarkFinishLounchWorkTime = Boolean(
    isTimeRegistrationBlocked ||
      !!dailyWork?.end_lounch ||
      !dailyWork?.begin ||
      !!dailyWork?.finish ||
      dailyWork?.type === WorkType.JUSTIFICATION ||
      (dailyWork?.begin && dailyWork?.type === WorkType.SCHEDULED),
  );

  const canMarkFinishWorkTime = Boolean(
    isTimeRegistrationBlocked ||
      !!dailyWork?.finish ||
      !dailyWork?.begin ||
      dailyWork?.type === WorkType.JUSTIFICATION ||
      (dailyWork?.begin && dailyWork?.type === WorkType.SCHEDULED),
  );

  const [sectorModal, setSectorModal] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [sectorSelected, setSectorSelected] = useState<string | null>(null);
  const [sectorOptions, setSectorOptions] = useState(SECTOR_OPTIONS);
  const [modalFilterCode, setModalFilterCode] = useState('');
  const [modalFilterSector, setModalFilterSector] = useState<string | null>(
    null,
  );

  const {
    fetch: fetchSectorLog,
    log: sectorLog,
    loading: sectorLogLoading,
  } = useGetSectorDailyLog();
  const { upsert: upsertSectorLog, loading: sectorSaving } =
    useUpsertSectorDailyLog();
  const {
    fetch: fetchUsers,
    response: usersData,
    loading: loadingUsers,
  } = useGetUsersPaginated();

  const {
    fetch: getEmployeeFetcher,
    response: getEmployeeResponse,
    loading: getEmployeeLoader,
  } = useGetEmployee();

  // Open record from a previous day: forgot to punch out. Night workers legitimately carry one over.
  const hasStaleOpenRecord = Boolean(
    dailyWork?.type === WorkType.WORK &&
      dailyWork.begin &&
      !dailyWork.finish &&
      !dayjs(dailyWork.begin).isSame(dayjs(), 'day') &&
      getEmployeeResponse?.period !== EmployeePeriod.NIGHT,
  );

  async function handleGetEmployee() {
    await getEmployeeFetcher({
      id: Number(user?.user.id),
    });
  }

  function validateWorkInterval(historyTime?: string) {
    if (dayjsPlugins(new Date()).diff(historyTime, 'minutes') < 20) {
      errorNotification({
        title: 'Você não atingiu o tempo mínimo.',
        message: 'necessário 20 minutos de intervalo entre os registros',
      });
      throw false;
    }
  }

  async function handleGetWorkHistory() {
    try {
      setPageLoading(true);
      const response = await getLastDailyWorkRequest(Number(user?.user.id), {
        date: new Date().toISOString(),
      });
      setDailyWork(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
    }
  }

  async function handleGetLocation() {
    try {
      setPageLoading(true);
      const addr = await getLocation();
      setAddress(addr);
    } catch (err) {
      console.error(err);
      setPageLoading(false);
      setAddress('Não foi possível obter o endereço');
    } finally {
      setPageLoading(false);
    }
  }

  async function handleSubmit(type: DailyWorkType) {
    // const allowedList = allowedLocations.map((item) => {
    //   const distanceInKm = getDistanceFromLatLonInKm({
    //     lat1: location.latitude,
    //     lon1: location.longitude,
    //     lat2: item.latitude,
    //     lon2: item.longitude,
    //   });

    //   if (distanceInKm > 5) {
    //     return false;
    //   } else {
    //     return true;
    //   }
    // });

    // if (
    //   !envs.devEnvironment &&
    //   ![1466, 584, 811, 548, 530, 1905].includes(Number(user?.user.id)) &&
    //   !allowedList.includes(true)
    // ) {
    //   errorNotification({
    //     title: 'Você esta fora da área permitida.',
    //     message: 'necessário estar na área da empresa',
    //   });
    //   return;
    // }

    setPageConfirm(null);
    try {
      if (type === DailyWorkType.END_LOUNCH) {
        validateWorkInterval(dailyWork?.start_lounch);
      }

      setPageLoading(true);
      if (type === DailyWorkType.BEGIN) {
        if (dailyWork?.type === WorkType.SCHEDULED) {
          const response = await changeScheduled(Number(dailyWork?.id));
          setDailyWork(response);
          setPageLoading(false);
          setPageModal('confirm-daily-work-create');
          return;
        }
        const response = await createDailyWorkRequest({
          location: address ?? 'Não foi possível obter o endereço',
        });
        setDailyWork(response);
      } else {
        const response = await updateDailyWorkRequest(Number(dailyWork?.id), {
          type,
          location: address ?? 'Não foi possível obter o endereço',
        });
        setDailyWork(response);
      }
      setPageLoading(false);
      setPageModal('confirm-daily-work-create');
    } catch (error: any) {
      errorNotification({
        title: translateServerHttpErrors(error, 'Erro ao registrar horário.'),
        message: 'tente novamente',
      });
      setPageLoading(false);
    }
  }

  useEffect(() => {
    const interval = setInterval(() => setCurrentHour(new Date()), 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (
      user?.user.isSectorSupervisor &&
      getEmployeeResponse?.sector &&
      !sectorSelected
    ) {
      setSectorSelected(getEmployeeResponse.sector);
    }
  }, [user?.user.isSectorSupervisor, getEmployeeResponse?.sector]); // eslint-disable-line

  useEffect(() => {
    if (user?.user.isSectorSupervisor && sectorSelected) {
      fetchSectorLog(dayjs().format('YYYY-MM-DD'), sectorSelected);
    }
  }, [user?.user.isSectorSupervisor, sectorSelected]); // eslint-disable-line

  useEffect(() => {
    setSelectedCodes(new Set(sectorLog?.employeeCodes ?? []));
  }, [sectorLog]);

  function openSectorModal() {
    setSelectedCodes(new Set(sectorLog?.employeeCodes ?? []));
    setModalFilterCode('');
    setModalFilterSector(null);
    fetchUsers({ query: { page: 1, limit: 500 } });
    setSectorModal(true);
  }

  function toggleCode(code: string) {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }

  function saveSectorLog() {
    if (!sectorSelected) return;
    upsertSectorLog(
      {
        date: dayjs().format('YYYY-MM-DD'),
        sector: sectorSelected,
        employeeCodes: Array.from(selectedCodes),
      },
      () => {
        setSectorModal(false);
        fetchSectorLog(dayjs().format('YYYY-MM-DD'), sectorSelected);
      },
    );
  }

  const filteredUsers = useMemo(() => {
    const codeLower = modalFilterCode.trim().toLowerCase();
    return (usersData?.items ?? []).filter((u) => {
      if (codeLower && !u.accessCode?.toLowerCase().includes(codeLower))
        return false;
      if (modalFilterSector && (u as any).sector !== modalFilterSector)
        return false;
      return true;
    });
  }, [usersData, modalFilterCode, modalFilterSector]);

  useEffect(() => {
    handleGetWorkHistory();
    handleGetEmployee();
    handleGetLocation();
    listOptionsRequest('sector').then((opts) => {
      setSectorOptions((prev) => {
        const existing = new Set(prev.map((o) => o.value));
        const extra = opts
          .filter((o) => !existing.has(o.name))
          .map((o) => ({ value: o.name, label: o.alias ?? o.name }));
        return extra.length ? [...prev, ...extra] : prev;
      });
    });
  }, []);

  return (
    <Page>
      <CustomLoader loading={pageLoading || getEmployeeLoader} />
      <Group mb={8}>
        <Avatar src={null} alt="no image here" color="indigo" />
        <Text>
          Olá,{' '}
          <Text fw="bold" span c="indigo" inherit>
            USU{user?.user.identificator}
          </Text>
        </Text>
      </Group>
      <Grid columns={3}>
        <Grid.Col span={2}>
          <Card h={'75vh'} p={16}>
            <Title size={20} mb={'xs'}>
              Registro de Horários ⏱️
            </Title>

            {dailyWork?.type === WorkType.SCHEDULED ? (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Atenção"
                color="yellow"
                mt={'lg'}
                mb={'lg'}
              >
                Existe uma <b>justificativa de banco de horas</b> registrada
                para o dia de hoje. Antes de bater o ponto, verifique se essa
                justificativa ainda é válida ou se necessita de atualização.
              </Alert>
            ) : (
              ''
            )}

            {user?.user.canRegisterTime === false && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Registro Bloqueado"
                color="red"
                mt={'lg'}
                mb={'lg'}
              >
                {user?.user.registerTimeBlockMessage ||
                  'Você não tem permissão para realizar o registro de ponto no momento. Entre em contato com o RH para a regularização.'}
              </Alert>
            )}

            {hasStaleOpenRecord && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Saída não registrada"
                color="red"
                mt={'lg'}
                mb={'lg'}
              >
                Você não registrou a <b>saída</b> do dia{' '}
                {formatLocale(dailyWork?.begin ?? '', 'DD/MM/YYYY')}. Antes de
                bater o ponto, dirija-se ao <b>RH</b> para regularizar sua
                situação.
              </Alert>
            )}

            <Title align="center">{currentHour.toLocaleTimeString()}</Title>
            <Title size={18} align="center" mb={'xs'}>
              {dayjsPlugins().format('ddd[,] DD [de] MMMM')}
            </Title>
            <Text align="center" mb={16}>
              Clique no botão correspondente ao horário que deseja registrar.
            </Text>
            {dailyWork?.type === WorkType.JUSTIFICATION && (
              <Text align="center" mb={16} color="red" fw={'bold'} size={'md'}>
                Justificativa: {dailyWork.justification}
              </Text>
            )}
            <Flex justify="center" wrap="wrap">
              <Flex direction="column" align="center" mr={16}>
                <Popover
                  opened={pageConfirm === 'confirm-begin'}
                  width={225}
                  position="bottom"
                  withArrow
                  shadow="md"
                >
                  <Popover.Target>
                    <Button
                      disabled={canMarkBeginWorkTime || hasStaleOpenRecord}
                      onClick={() => setPageConfirm('confirm-begin')}
                      color="green"
                      rightIcon={<IconDoorEnter size={20} />}
                    >
                      Entrada
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Text size="sm">
                      Deseja realmente registrar "Entrada" ?
                    </Text>
                    <Flex>
                      <Button
                        loading={pageLoading}
                        onClick={() => handleSubmit(DailyWorkType.BEGIN)}
                        mr={8}
                        color="green"
                      >
                        Sim
                      </Button>
                      <Button onClick={() => setPageConfirm(null)} color="red">
                        Não
                      </Button>
                    </Flex>
                  </Popover.Dropdown>
                </Popover>
                <Text>
                  {dailyWork?.begin
                    ? formatLocale(dailyWork?.begin, 'DD/MM/YY HH:mm')
                    : ''}
                </Text>
              </Flex>
              <Flex direction="column" align="center" mr={16}>
                <Popover
                  opened={pageConfirm === 'confirm-start-lounch'}
                  width={225}
                  position="bottom"
                  withArrow
                  shadow="md"
                >
                  <Popover.Target>
                    <Button
                      disabled={canMarkInitLounchWorkTime || hasStaleOpenRecord}
                      onClick={() => setPageConfirm('confirm-start-lounch')}
                      color="orange"
                      rightIcon={<IconClock size={20} />}
                    >
                      Inicio Intervalo
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Text size="sm">
                      Deseja realmente registrar "Inicio Intervalo" ?
                    </Text>
                    <Flex>
                      <Button
                        loading={pageLoading}
                        onClick={() => handleSubmit(DailyWorkType.START_LOUNCH)}
                        mr={8}
                        color="green"
                      >
                        Sim
                      </Button>
                      <Button onClick={() => setPageConfirm(null)} color="red">
                        Não
                      </Button>
                    </Flex>
                  </Popover.Dropdown>
                </Popover>
                <Text>
                  {dailyWork?.start_lounch
                    ? formatLocale(dailyWork?.start_lounch, 'DD/MM/YY HH:mm')
                    : ''}
                </Text>
              </Flex>
              <Flex direction="column" align="center" mr={16}>
                <Popover
                  opened={pageConfirm === 'confirm-end-lounch'}
                  width={225}
                  position="bottom"
                  withArrow
                  shadow="md"
                >
                  <Popover.Target>
                    <Button
                      disabled={
                        canMarkFinishLounchWorkTime || hasStaleOpenRecord
                      }
                      onClick={() => setPageConfirm('confirm-end-lounch')}
                      color="orange.4"
                      rightIcon={<IconClockCancel size={20} />}
                    >
                      Fim Intervalo
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Text size="sm">
                      Deseja realmente registrar "Fim Intervalo" ?
                    </Text>
                    <Flex>
                      <Button
                        loading={pageLoading}
                        onClick={() => handleSubmit(DailyWorkType.END_LOUNCH)}
                        mr={8}
                        color="green"
                      >
                        Sim
                      </Button>
                      <Button onClick={() => setPageConfirm(null)} color="red">
                        Não
                      </Button>
                    </Flex>
                  </Popover.Dropdown>
                </Popover>
                <Text>
                  {dailyWork?.end_lounch
                    ? formatLocale(dailyWork?.end_lounch, 'DD/MM/YY HH:mm')
                    : ''}
                </Text>
              </Flex>
              <Flex direction="column" align="center" mr={16}>
                <Popover
                  opened={pageConfirm === 'confirm-finish'}
                  width={225}
                  position="bottom"
                  withArrow
                  shadow="md"
                >
                  <Popover.Target>
                    <Button
                      disabled={canMarkFinishWorkTime || hasStaleOpenRecord}
                      onClick={() => setPageConfirm('confirm-finish')}
                      color="red"
                      rightIcon={<IconDoorExit size={20} />}
                    >
                      Saída
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Text size="sm">Deseja realmente registrar "Saída" ?</Text>
                    <Flex>
                      <Button
                        loading={pageLoading}
                        onClick={() => handleSubmit(DailyWorkType.FINISH)}
                        mr={8}
                        color="green"
                      >
                        Sim
                      </Button>
                      <Button onClick={() => setPageConfirm(null)} color="red">
                        Não
                      </Button>
                    </Flex>
                  </Popover.Dropdown>
                </Popover>
                <Text>
                  {dailyWork?.finish
                    ? formatLocale(dailyWork?.finish, 'DD/MM/YY HH:mm')
                    : ''}
                </Text>
              </Flex>
            </Flex>
          </Card>
        </Grid.Col>
        <Grid.Col span={1}>
          <Flex direction="column" gap={16}>
            {user?.user.role !== UserRole.DAILY_EMPLOYEE && (
              <BankHourRequest
                pageModal={pageModal}
                setPageModal={setPageModal}
                userId={Number(user?.user.id)}
              />
            )}

            {user?.user.isSectorSupervisor && (
              <Card mb={16}>
                <Group position="apart" mb={8}>
                  <Title size={20}>
                    <IconUsers
                      size={18}
                      style={{ marginRight: 6, verticalAlign: 'middle' }}
                    />
                    {sectorSelected ?? 'Setor'} — hoje
                  </Title>
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={openSectorModal}
                    title="Editar lista"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Group>

                {!sectorSelected && (
                  <Text size="sm" color="dimmed">
                    Nenhum setor selecionado.
                  </Text>
                )}

                {sectorSelected && sectorLogLoading && (
                  <Text size="sm" color="dimmed">
                    Carregando...
                  </Text>
                )}

                {sectorSelected &&
                  !sectorLogLoading &&
                  (!sectorLog || sectorLog.employeeCodes.length === 0) && (
                    <Text size="sm" color="dimmed">
                      Nenhum funcionário registrado hoje.
                    </Text>
                  )}

                {sectorSelected &&
                  sectorLog &&
                  sectorLog.employeeCodes.length > 0 && (
                    <Group spacing={6}>
                      {sectorLog.employeeCodes.map((code) => (
                        <Badge key={code} size="sm" variant="outline">
                          {code}
                        </Badge>
                      ))}
                    </Group>
                  )}
              </Card>
            )}

            <Card>
              <Title size={20}>Comunicados 📋</Title>
              <ScrollArea mt={16} h={350}>
                {getEmployeeResponse?.epis &&
                getEmployeeResponse.epis.length === 0 ? (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Atenção: EPIs"
                    color="orange.5"
                    variant="filled"
                  >
                    Há assinatura de EPI`s pendentes. Por favor, dirija-se ao
                    setor de RH para regularizar sua situação.
                  </Alert>
                ) : (
                  <Card bg="gray.2">
                    <Text>Sem comunicados.</Text>
                  </Card>
                )}
              </ScrollArea>
            </Card>
          </Flex>
        </Grid.Col>
      </Grid>
      <Modal
        title="Registro de Horário."
        opened={pageModal === 'confirm-daily-work-create'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        withCloseButton={false}
        closeOnEscape={false}
      >
        <Group>
          <Text fw="bold">Tudo certo!</Text>
          <Text>você será deslogado</Text>
          <Text>{dayjsPlugins().format('DD/MM/YYYY HH:mm:ss')}</Text>
        </Group>
        <Group position="right">
          <Button onClick={() => logout()}>OK</Button>
        </Group>
      </Modal>
      <Modal
        title={`Funcionários — ${sectorSelected ?? 'Setor'} (${dayjs().format(
          'DD/MM/YYYY',
        )})`}
        opened={sectorModal}
        onClose={() => setSectorModal(false)}
        size="lg"
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Stack spacing="sm">
          <Select
            label="Setor"
            placeholder="Selecione ou digite o setor..."
            data={sectorOptions}
            value={sectorSelected}
            onChange={setSectorSelected}
            searchable
            creatable
            getCreateLabel={(q) => `+ Usar "${q}"`}
            onCreate={(q) => {
              const item = { value: q, label: q };
              setSectorOptions((prev) => [...prev, item]);
              createOptionRequest({ name: q, type: 'sector' });
              return item;
            }}
          />

          <Divider
            label="Selecionar funcionários"
            labelPosition="left"
            mt={4}
          />

          <Group spacing="xs">
            <TextInput
              placeholder="Filtrar por código"
              value={modalFilterCode}
              onChange={(e) => setModalFilterCode(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Filtrar por setor"
              data={sectorOptions}
              clearable
              value={modalFilterSector}
              onChange={setModalFilterSector}
              style={{ width: 180 }}
            />
          </Group>

          <Group
            spacing="xs"
            px={8}
            py={4}
            style={{
              background: 'var(--mantine-color-gray-1,#f1f3f5)',
              borderRadius: 6,
            }}
            noWrap
          >
            <div style={{ width: 24 }} />
            <Text size="xs" fw={600} style={{ width: 120 }}>
              Código
            </Text>
            <Text size="xs" fw={600} style={{ flex: 1 }}>
              Setor
            </Text>
          </Group>

          <ScrollArea h={220} offsetScrollbars>
            {loadingUsers && (
              <Text size="sm" color="dimmed" px={8}>
                Carregando...
              </Text>
            )}
            {!loadingUsers && filteredUsers.length === 0 && (
              <Text size="sm" color="dimmed" px={8}>
                Nenhum funcionário encontrado.
              </Text>
            )}
            {filteredUsers.map((u) => (
              <Group
                key={u.accessCode}
                spacing="xs"
                px={8}
                py={6}
                noWrap
                style={{
                  borderBottom: '1px solid var(--mantine-color-gray-2,#e9ecef)',
                  cursor: 'pointer',
                  background: selectedCodes.has(u.accessCode)
                    ? 'var(--mantine-color-blue-0,#e7f5ff)'
                    : undefined,
                }}
                onClick={() => toggleCode(u.accessCode)}
              >
                <Checkbox
                  checked={selectedCodes.has(u.accessCode)}
                  onChange={() => toggleCode(u.accessCode)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: 24 }}
                />
                <Text size="sm" style={{ width: 120, fontFamily: 'monospace' }}>
                  {u.accessCode}
                </Text>
                <Text size="sm" color="dimmed" style={{ flex: 1 }}>
                  {(u as any).sector ?? '—'}
                </Text>
              </Group>
            ))}
          </ScrollArea>

          <Group position="apart">
            <Text size="xs" color="dimmed">
              {selectedCodes.size} selecionado
              {selectedCodes.size !== 1 ? 's' : ''}
            </Text>
            <Group spacing="xs">
              <Button variant="default" onClick={() => setSectorModal(false)}>
                Cancelar
              </Button>
              <Button loading={sectorSaving} onClick={saveSectorLog}>
                Salvar
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      <Modal
        title="Acesso Negado."
        opened={deviceType === 'Mobile'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        withCloseButton={false}
        closeOnEscape={false}
      >
        <Group>
          <Text fw="bold">Opa!</Text>
          <Text>
            Você não pode utlizar este sistema a partir de um celular.
          </Text>
        </Group>
        <Group position="right">
          <Button onClick={() => logout()}>OK</Button>
        </Group>
      </Modal>
      {/* <Modal
        size={600}
        title="Acesso a Localização."
        opened={permissionStatus === 'denied'}
        onClose={() => setPageModal(false)}
        closeOnClickOutside={false}
        withCloseButton={false}
        closeOnEscape={false}
      >
        <Group mb={16}>
          <Text fw="bold">Localização necessária!</Text>
          <Text>
            Você não pode registrar o ponto sem dar acesso a localização veja
            como fazer:
          </Text>
        </Group>
        <Image
          mb={16}
          src="https://orders-files.nyc3.cdn.digitaloceanspaces.com/media/user-activate-location.gif"
        />
        <Group>
          <Image />
        </Group>
      </Modal> */}
    </Page>
  );
}
